import * as React from 'react';
import { graphql } from 'react-apollo';

import { branch, compose } from 'recompose';

import { Link } from '@coursera/cds-core';
import { SvgButton } from '@coursera/coursera-ui';
import { SvgShare } from '@coursera/coursera-ui/svg';

import CourseMetadataQuery from 'bundles/accomplishments/components/pdf-page/CourseMetadataQuery';
import S12nMetadataQuery from 'bundles/accomplishments/components/pdf-page/S12nMetadataQuery';
import { shareUtmContentParam, shareUtmMediumParam } from 'bundles/accomplishments/constants';
import getSocialCaptions from 'bundles/accomplishments/utils/getSocialCaptions';
import { getCourseCertProductUtm } from 'bundles/accomplishments/utils/utils';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { getS12nCertProductUtm } from 'bundles/s12n-common/lib/s12nProductVariantUtils';
import ShareButtonWithModal from 'bundles/sharing-common/components/modal/ShareButtonWithModal';

import _t from 'i18n!nls/accomplishments';

import 'css!bundles/accomplishments/components/pdf-page/__styles__/PdfPageHeader';

type PropsFromCaller = {
  shareLink: string;
  certType: string;
  pdfDownloadLink: string;
  landingPageLink: string;
  productName?: string;
  partnerName?: string;
  slug?: string;
  isS12n: boolean;
};

type PropsToComponent = PropsFromCaller & {
  utmProductParam?: string;
};

const PdfPageHeader = ({ certType, partnerName, productName, landingPageLink, utmProductParam }: PropsToComponent) => {
  return (
    <div className="rc-PdfPageHeader">
      <div className="rc-PdfPageHeader__container">
        <TrackedDiv className="back-button" trackingName="pdf_header_link_back_to_landing">
          <Link invert variant="quiet" href={landingPageLink} className="rc-PdfPageHeader__link">
            <i className="cif-chevron-left" />
            &nbsp;&nbsp;
            <span>{_t('#{certType} Record', { certType })}</span>
          </Link>
        </TrackedDiv>
        <div className="rc-PdfPageHeader__share-column">
          <ShareButtonWithModal
            rootClassName="certificate-share-button"
            shareLink={landingPageLink}
            utmContentParam={shareUtmContentParam}
            utmMediumParam={shareUtmMediumParam}
            utmProductParam={utmProductParam}
            utmCampaignParam="pdf_header_button"
            captions={productName && partnerName ? getSocialCaptions({ productName, partnerName }) : undefined}
            useCustomUrl
          >
            <SvgButton
              type="primary"
              label={_t('Share')}
              tag="button"
              size="zero"
              svgElement={
                <SvgShare isThemeDark color="#2A73CC" hoverColor="#fff" stroke="#2A73CC" size={20} strokeWidth={1} />
              }
              style={{
                padding: '7px 8px',
              }}
              isThemeDark
              isChildrenOnRight
            />
          </ShareButtonWithModal>
        </div>
      </div>
    </div>
  );
};

export default compose<PropsToComponent, PropsFromCaller>(
  /* 
    Find certificate product type to attach as utm_product param.
    Have to make these calls to detect if product is a guided project or prof cert.
    The certificate data can only tell us if it's a course or a s12n. 
  */
  branch(
    ({ slug }: Pick<PropsFromCaller, 'slug'>) => !!slug,
    branch(
      ({ isS12n }: Pick<PropsFromCaller, 'isS12n'>) => !isS12n,
      // If cert is for a course
      compose(
        graphql(CourseMetadataQuery, {
          props: ({ data: { XdpV1Resource } }: any) => {
            const cdpMetadata = XdpV1Resource?.slug?.elements?.[0]?.xdpMetadata?.cdpMetadata;
            return {
              utmProductParam: getCourseCertProductUtm(cdpMetadata?.courseTypeMetadata),
            };
          },
        })
      ),
      // if cert is for a s12n
      compose(
        graphql(S12nMetadataQuery, {
          props: ({ data: { XdpV1Resource } }: any) => {
            const sdpMetadata = XdpV1Resource?.slug?.elements?.[0]?.xdpMetadata?.sdpMetadata;
            return {
              utmProductParam: getS12nCertProductUtm(sdpMetadata?.productVariant),
            };
          },
        })
      )
    )
  )
)(PdfPageHeader);
